import React, { useEffect, useState } from "react";
import AuthenticatedAvatar from "../../components/AuthenticatedAvatar";
import { Card, Row, Col, Spinner, Table } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  PeopleFill,
  PersonCheckFill,
  PersonLinesFill,
  FileEarmarkPlayFill,
  DatabaseFill,
  CalendarCheck,
  JournalBookmarkFill,
  GeoAltFill,
  People,
  GraphUp,
  StarFill,
  ClockHistory,
} from "react-bootstrap-icons";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Link,
  Typography,
  LinearProgress,
  CircularProgress,
  Tooltip as MuiTooltip,
} from "@mui/material";
import API_BASE_URL from "../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const Dashboard = () => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participationData, setParticipationData] = useState([]);
  const navigate = useNavigate();
  const [participationLoading, setParticipationLoading] = useState(true);
  const [dashboardCounts, setDashboardCounts] = useState(null);
  const [trendingInstructors, setTrendingInstructors] = useState([]);
  const [instructorsLoading, setInstructorsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Clock updater
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format date & time
  const formattedDateTime = currentTime.toLocaleString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");

    const fetchStats = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/admin/stats/overview`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) setStatsData(res.data.data);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

 const fetchParticipationData = async () => {
  try {
    const res = await axios.get(
      `${API_BASE_URL}/api/register/getParticipationOverview`,
    );

    if (res.data.success) {
      const data = res.data.data;

      // Example calculations
      const totalClasses = data.totalClasses || 0;
      const totalRegistrations = data.totalRegistrations || 0;
      const attendancePercentage =
        parseFloat(data.attendancePercentage) || 0;

      // Calculate registration per class percentage
      const registrationPercentage =
        totalClasses > 0
          ? Math.min(( totalClasses / totalRegistrations ) * 10, 100)
          : 0;

      // Example class completion percentage
      const classPercentage =
        totalClasses > 0
          ? Math.min((totalClasses / 100) * 100, 100)
          : 0;

      setParticipationData([
        {
          label: "Total Classes",
          value: classPercentage,
          color: "#00bcd4",
          displayValue: totalClasses,
        },
        {
          label: "Total Registration",
          value: registrationPercentage,
          color: "#ff9800",
          displayValue: totalRegistrations,
        },
        {
          label: "Attendance %",
          value: attendancePercentage,
          color: "#4caf50",
          displayValue: `${attendancePercentage}%`,
        },
      ]);
    }
  } catch (error) {
    console.error("Error fetching participation overview:", error);
  } finally {
    setParticipationLoading(false);
  }
};

    const fetchDashboardCounts = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/register/dashboardCounts`,
        );
        if (res.data.success) setDashboardCounts(res.data.data);
      } catch (error) {
        console.error("Error fetching dashboard counts:", error);
      }
    };

    const fetchTrendingInstructors = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/instructor/getTrendingInstructors`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setTrendingInstructors(res.data.success ? res.data.data || [] : []);
      } catch (error) {
        console.error("Error fetching trending instructors:", error);
        setTrendingInstructors([]);
      } finally {
        setInstructorsLoading(false);
      }
    };

    fetchStats();
    fetchParticipationData();
    fetchDashboardCounts();
    fetchTrendingInstructors();
  }, []);

  const stats = statsData
    ? [
        {
          title: "Active Users",
          value: statsData.activeUsers,
          color: "linear-gradient(135deg, #42a5f5, #1a237e)",
          icon: <PeopleFill size={24} />,
          path: "/users",
        },
        {
          title: "Active Mentors",
          value: statsData.activeMentors,
          color: "linear-gradient(135deg, #66bb6a, #2e7d32)",
          icon: <PersonCheckFill size={24} />,
          path: "/mentors",
        },
        {
          title: "Active Instructors",
          value: statsData.activeInstructors,
          color: "linear-gradient(135deg, #ffb74d, #f57c00)",
          icon: <PersonLinesFill size={24} />,
          path: "/instructors",
        },
        {
          title: "Total Classes",
          value: statsData.activeClasses,
          color: "linear-gradient(135deg, #ab47bc, #6a1b9a)",
          icon: <DatabaseFill size={24} />,
          path: "/data/classses",
        },
      ]
    : [];

  const dashboardStats = dashboardCounts
    ? [
        {
          title: "Upcoming Sessions",
          icon: <JournalBookmarkFill size={20} />,
          value: dashboardCounts.upcomingCount,
          path: "/data/classses",
        },
        {
          title: "Live Sessions",
          icon: <FileEarmarkPlayFill size={20} />,
          value: dashboardCounts.liveCount,
          path: "/data/classses",
        },
        {
          title: "Total Students",
          icon: <People size={20} />,
          value: dashboardCounts.totalPrisoners,
          path: "/students",
        },
        {
          title: "Total Locations",
          icon: <GeoAltFill size={20} />,
          value: dashboardCounts.totalLocations,
          path: "/data",
        },
      ]
    : [];

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Dashboard Overview
        </Typography>
      </Breadcrumbs>

      {/* Page header with clock */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <GraphUp size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Dashboard Overview
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Real-time analytics and metrics
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#fff",
            px: 2,
            py: 1,
            borderRadius: "20px",
            border: "1px solid #e4eaf8",
          }}
        >
          <CalendarCheck size={16} color="#0d47a1" />
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "#1a237e" }}
          >
            {formattedDateTime}
          </Typography>
        </Box>
      </Box>

      {/* Main Stats Cards */}
      <Row className="g-3 mb-4">
        {loading ? (
          <Col className="text-center py-5">
            <Spinner animation="border" style={{ color: "#0d47a1" }} />
          </Col>
        ) : (
          stats.map((item, idx) => (
            <Col key={idx} xs={12} sm={6} lg={3}>
              <Box
                onClick={() => navigate(item.path)}
                sx={{
                  background: item.color,
                  borderRadius: "14px",
                  p: 2.5,
                  color: "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 14px rgba(13,71,161,0.15)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 20px rgba(13,71,161,0.25)",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.7rem",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {item.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "10px",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>
              </Box>
            </Col>
          ))
        )}
      </Row>

      {/* Secondary Stats Cards */}
      <Row className="g-3 mb-4">
        {dashboardStats.map((item, idx) => (
          <Col key={idx} xs={12} sm={6} lg={3}>
            <Box
              onClick={() => navigate(item.path)}
              sx={{
                background: "#fff",
                borderRadius: "12px",
                p: 2,
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "1px solid #e4eaf8",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 15px rgba(13,71,161,0.1)",
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    background: "#f0f4ff",
                    borderRadius: "10px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#0d47a1",
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#7b8ab8", fontWeight: 600 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#1a237e", lineHeight: 1.2 }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Col>
        ))}
      </Row>

      {/* Participation Overview */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          p: 3,
          mb: 4,
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GraphUp size={16} color="#fff" />
          </Box>
          <Typography
            sx={{ fontWeight: 700, color: "#1a237e", fontSize: "0.95rem" }}
          >
            Participation Overview
          </Typography>
        </Box>

        {participationLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <Spinner animation="border" style={{ color: "#0d47a1" }} />
          </Box>
        ) : (
          <Row>
            {participationData.map((item, idx) => (
              <Col key={idx} xs={12} md={4}>
                <Box textAlign="center" px={2}>
                  <Box position="relative" display="inline-flex" mb={1}>
                    {/* <CircularProgress
                      variant="determinate"
                      value={item.value}
                      size={90}
                      thickness={4}
                      sx={{
                        color: item.color,
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        },
                      }}
                    /> */}
                    {/* <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "#1a237e" }}
                      >
                        {Math.round(item.value)}%
                      </Typography>
                    </Box> */}
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#1a237e", mt: 1 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#0d47a1" }}
                  >
                    {item.displayValue}
                  </Typography>
                  {/* <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={item.value}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "#e4eaf8",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: item.color,
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box> */}
                </Box>
              </Col>
            ))}
          </Row>
        )}
      </Box>

      {/* Trending Instructors - Table View */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
          mb: 4,
        }}
      >
        <Box
          sx={{
            p: 2.5,
            borderBottom: "1px solid #e4eaf8",
            background: "#fafbff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                background: primaryGradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StarFill size={16} color="#fff" />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: "#1a237e", fontSize: "0.95rem" }}
            >
              Trending Instructors
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <div className="table-responsive">
            {instructorsLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <Spinner animation="border" style={{ color: "#0d47a1" }} />
              </Box>
            ) : trendingInstructors.length > 0 ? (
              <Table hover className="mb-0 align-middle">
                <thead style={{ background: primaryGradient }}>
                  <tr>
                    {[
                      "#",
                      "Instructor",
                      "Location",
                      "Classes",
                      "Sessions",
                      "Registrations",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          color: "rgba(255,255,255,0.85)",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          padding: "12px 16px",
                          border: "none",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trendingInstructors.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f4ff" }}>
                      <td
                        style={{
                          color: "#aab4cc",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {idx + 1}
                      </td>
                      <td>
                        <Box display="flex" alignItems="center" gap={2}>
                          <AuthenticatedAvatar
                            src={item.profilePicture}
                            alt={item.name}
                            sx={{
                              width: 40,
                              height: 40,
                              border: "2px solid #e4eaf8",
                            }}
                          />

                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "#1a237e",
                              fontSize: "0.84rem",
                            }}
                          >
                            {item.name || "N/A"}
                          </Typography>
                        </Box>
                      </td>
                      <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                        {item.location || "N/A"}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0d47a1" }}>
                        {item.numClasses || 0}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0d47a1" }}>
                        {item.totalSessions || 0}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0d47a1" }}>
                        {item.registrations || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Box textAlign="center" py={3}>
                <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                  No data available
                </Typography>
              </Box>
            )}
          </div>
        </Box>
      </Box>

      {/* Trending Instructors - Chart View */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        <Box
          sx={{
            p: 2.5,
            borderBottom: "1px solid #e4eaf8",
            background: "#fafbff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                background: primaryGradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GraphUp size={16} color="#fff" />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: "#1a237e", fontSize: "0.95rem" }}
            >
              Instructor Performance Chart
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {instructorsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <Spinner animation="border" style={{ color: "#0d47a1" }} />
            </Box>
          ) : trendingInstructors.length > 0 ? (
            <Line
              data={{
                labels: ["Classes", "Sessions", "Registrations"],
                datasets: trendingInstructors.map((instr, idx) => ({
                  label: instr.name || "N/A",
                  data: [
                    instr.numClasses || 0,
                    instr.totalSessions || 0,
                    instr.registrations || 0,
                  ],
                  borderColor: `hsl(${idx * 50}, 70%, 50%)`,
                  backgroundColor: `hsla(${idx * 50}, 70%, 50%, 0.1)`,
                  tension: 0.4,
                  fill: true,
                  pointRadius: 5,
                  pointHoverRadius: 8,
                  pointBorderColor: "#fff",
                  pointBorderWidth: 2,
                })),
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top",
                    labels: {
                      font: { size: 12 },
                      usePointStyle: true,
                      boxWidth: 8,
                    },
                  },
                  tooltip: {
                    backgroundColor: "#1a237e",
                    titleColor: "#fff",
                    bodyColor: "rgba(255,255,255,0.8)",
                    borderColor: "#0d47a1",
                    borderWidth: 1,
                  },
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: { size: 11, weight: 600 },
                      color: "#7b8ab8",
                    },
                  },
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: "#e4eaf8",
                    },
                    ticks: {
                      font: { size: 11 },
                      color: "#7b8ab8",
                      stepSize: 1,
                    },
                  },
                },
              }}
              height={300}
            />
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                No chart data available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
